import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, ROLE_LABEL } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { Trash2, UserPlus, Users as UsersIcon, RefreshCw, Shield, KeyRound, Lock, Pencil } from "lucide-react";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    role: "kurye",
    display_name: "",
    username: "",
    password: "",
  });

  // Sipariş personeli (PIN tabanlı, login'siz)
  const [clerks, setClerks] = useState([]);
  const [clerkForm, setClerkForm] = useState({ name: "", pin: "" });
  const [clerkBusy, setClerkBusy] = useState(false);

  const fetchAll = async () => {
    try {
      const [u, c] = await Promise.all([
        api.get("/users"),
        api.get("/clerks"),
      ]);
      setUsers(u.data);
      setClerks(c.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const createClerk = async (e) => {
    e.preventDefault();
    if (!clerkForm.name.trim() || clerkForm.pin.length < 4) {
      toast.error("Ad ve en az 4 haneli şifre girin");
      return;
    }
    setClerkBusy(true);
    try {
      const { data } = await api.post("/clerks", { name: clerkForm.name.trim(), pin: clerkForm.pin });
      toast.success(`${data.name} eklendi`);
      setClerks((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setClerkForm({ name: "", pin: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClerkBusy(false);
    }
  };

  const removeClerk = async (c) => {
    if (!window.confirm(`${c.name} adlı sipariş personelini silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/clerks/${c.id}`);
      setClerks((prev) => prev.filter((x) => x.id !== c.id));
      toast.success("Sipariş personeli silindi");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeClerkPin = async (c) => {
    const newPin = window.prompt(`${c.name} için yeni şifre (en az 4 hane):`);
    if (newPin === null) return;
    if (newPin.length < 4) {
      toast.error("Şifre en az 4 hane olmalı");
      return;
    }
    try {
      await api.put(`/clerks/${c.id}`, { pin: newPin });
      toast.success(`${c.name} şifresi güncellendi`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.display_name || !form.username || !form.password) {
      toast.error("Lütfen tüm alanları doldurun");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/users", {
        role: form.role,
        display_name: form.display_name.trim(),
        username: form.username.trim().toLowerCase(),
        password: form.password,
      });
      toast.success(`${data.display_name} eklendi`);
      setUsers((prev) => [...prev, data]);
      setForm({ role: "kurye", display_name: "", username: "", password: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`${u.display_name} adlı kullanıcıyı silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success("Kullanıcı silindi");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeUserPassword = async (u) => {
    const newPass = window.prompt(`${u.display_name} için yeni şifre (en az 4 karakter):`);
    if (newPass === null) return;
    if (newPass.length < 4) {
      toast.error("Şifre en az 4 karakter olmalı");
      return;
    }
    try {
      await api.put(`/users/${u.id}`, { password: newPass });
      toast.success(`${u.display_name} şifresi güncellendi`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeUserDisplayName = async (u) => {
    const newName = window.prompt(`${u.display_name} için yeni görünen ad:`, u.display_name);
    if (newName === null) return;
    const trimmed = newName.trim();
    if (!trimmed) {
      toast.error("Görünen ad boş olamaz");
      return;
    }
    if (trimmed === u.display_name) return;
    try {
      const { data } = await api.put(`/users/${u.id}`, { display_name: trimmed });
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, display_name: data.display_name } : x));
      toast.success(`${data.display_name} güncellendi`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const grouped = {
    admin:    users.filter((u) => u.role === "admin"),
    personel: users.filter((u) => u.role === "personel"),
    kurye:    users.filter((u) => u.role === "kurye"),
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <AppHeader
        title="Kullanıcılar"
        subtitle="Personel & Kurye Yönetimi"
        right={
          <button
            data-testid="refresh-button"
            onClick={fetchAll}
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 border-2 border-zinc-200 hover:border-zinc-950 transition-colors text-xs font-bold uppercase tracking-wider"
          >
            <RefreshCw size={14} strokeWidth={2.5} /> Yenile
          </button>
        }
      />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Create form + Clerk management */}
        <section className="lg:col-span-1 space-y-6 self-start">
          <div className="bg-white border-2 border-zinc-950 brutal-shadow-dark p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-zinc-950 text-white flex items-center justify-center">
              <UserPlus strokeWidth={3} size={16} />
            </div>
            <h2 className="text-lg font-black tracking-tight text-zinc-950">Yeni Kullanıcı</h2>
          </div>
          <form onSubmit={create} className="space-y-3" data-testid="new-user-form">
            <Field label="Rol">
              <select
                data-testid="form-role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="brutalist-input"
              >
                <option value="kurye">Kurye</option>
                <option value="personel">Personel</option>
              </select>
            </Field>
            <Field label="Görünen Ad *">
              <input
                data-testid="form-display-name"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                placeholder="Mehmet"
                className="brutalist-input"
                required
              />
            </Field>
            <Field label="Kullanıcı Adı *">
              <input
                data-testid="form-username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="mehmet"
                className="brutalist-input"
                pattern="[a-z0-9_.\-]{3,32}"
                required
              />
              <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-1">
                3-32 karakter • a-z, 0-9, _ . -
              </div>
            </Field>
            <Field label="Şifre *">
              <input
                data-testid="form-password"
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••"
                className="brutalist-input"
                minLength={4}
                required
              />
            </Field>
            <button
              data-testid="create-user-button"
              type="submit"
              disabled={creating}
              className="w-full bg-zinc-950 text-white text-sm font-black uppercase tracking-widest py-3 border-b-4 border-black active:border-b-0 active:translate-y-[3px] transition-all disabled:opacity-60"
            >
              {creating ? "Kaydediliyor…" : "Kullanıcı Ekle"}
            </button>
          </form>
          </div>

          {/* Sipariş Personeli (PIN, login'siz) */}
          <div className="bg-white border-2 border-zinc-950 brutal-shadow-dark p-5" data-testid="clerk-admin-section">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-violet-700 text-white flex items-center justify-center">
                <KeyRound strokeWidth={3} size={16} />
              </div>
              <h2 className="text-lg font-black tracking-tight text-zinc-950">Sipariş Personeli</h2>
            </div>
            <p className="text-[11px] text-zinc-600 leading-relaxed mb-3">
              Ortak cihazdan sipariş girişi yapan personel. Login&apos;siz; her sipariş kaydında kendi şifrelerini girerler.
            </p>
            <form onSubmit={createClerk} className="space-y-3" data-testid="new-clerk-form">
              <Field label="Ad *">
                <input
                  data-testid="clerk-name-input"
                  value={clerkForm.name}
                  onChange={(e) => setClerkForm({ ...clerkForm, name: e.target.value })}
                  placeholder="Ahmet"
                  className="brutalist-input"
                  required
                />
              </Field>
              <Field label="Şifre *">
                <input
                  data-testid="clerk-pin-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={clerkForm.pin}
                  onChange={(e) => setClerkForm({ ...clerkForm, pin: e.target.value.replace(/[^0-9]/g, "") })}
                  placeholder="••••"
                  className="brutalist-input font-mono tracking-widest"
                  minLength={4}
                  maxLength={12}
                  required
                />
                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-1">
                  4-12 hane • Yalnızca rakam
                </div>
              </Field>
              <button
                data-testid="create-clerk-button"
                type="submit"
                disabled={clerkBusy}
                className="w-full bg-violet-700 text-white text-sm font-black uppercase tracking-widest py-3 border-b-4 border-violet-900 active:border-b-0 active:translate-y-[3px] transition-all disabled:opacity-60"
              >
                {clerkBusy ? "Kaydediliyor…" : "Sipariş Personeli Ekle"}
              </button>
            </form>

            <div className="mt-4 pt-4 border-t-2 border-zinc-100">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-black tracking-tight text-zinc-950 text-sm">Mevcut Personel</h3>
                <span className="font-mono text-xs text-zinc-500" data-testid="clerk-count">{clerks.length}</span>
              </div>
              {clerks.length === 0 ? (
                <div className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400 py-4">Boş</div>
              ) : (
                <ul data-testid="clerk-admin-list" className="space-y-1.5">
                  {clerks.map((c) => (
                    <li
                      key={c.id}
                      data-testid={`clerk-${c.id}`}
                      className="px-3 py-2 border-2 border-zinc-100 flex items-center justify-between gap-2 hover:border-zinc-300 transition-colors"
                    >
                      <div className="min-w-0 flex-1 flex items-center gap-2">
                        <Lock size={11} className="text-zinc-400 shrink-0" strokeWidth={2.5} />
                        <span className="font-bold text-sm text-zinc-950 break-words">{c.name}</span>
                      </div>
                      <button
                        data-testid={`change-clerk-pin-${c.id}`}
                        onClick={() => changeClerkPin(c)}
                        className="p-1.5 text-zinc-400 hover:text-violet-700 hover:bg-violet-50 transition-colors"
                        title="Şifreyi değiştir"
                      >
                        <KeyRound size={13} strokeWidth={2.5} />
                      </button>
                      <button
                        data-testid={`delete-clerk-${c.id}`}
                        onClick={() => removeClerk(c)}
                        className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Sil"
                      >
                        <Trash2 size={13} strokeWidth={2.5} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>

        {/* List */}
        <section className="lg:col-span-2 space-y-6">
          {loading ? (
            <div className="border-2 border-dashed border-zinc-200 p-10 text-center font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
              Yükleniyor…
            </div>
          ) : (
            <>
              <UserList title="Yöneticiler" icon={<Shield strokeWidth={2.5} size={16} />} users={grouped.admin} meId={me?.id} onRemove={remove} onChangePassword={changeUserPassword} onRenameUser={changeUserDisplayName} canRemove={false} testIdPrefix="admin" />
              <UserList title="Personel" icon={<UsersIcon strokeWidth={2.5} size={16} />} users={grouped.personel} meId={me?.id} onRemove={remove} onChangePassword={changeUserPassword} onRenameUser={changeUserDisplayName} canRemove={true} testIdPrefix="personel" />
              <UserList title="Kuryeler" icon={<UsersIcon strokeWidth={2.5} size={16} />} users={grouped.kurye} meId={me?.id} onRemove={remove} onChangePassword={changeUserPassword} onRenameUser={changeUserDisplayName} canRemove={true} testIdPrefix="kurye" />
            </>
          )}
        </section>
      </main>

      <style>{`
        .brutalist-input {
          width: 100%;
          background-color: #fafafa;
          border: 2px solid #e4e4e7;
          padding: 0.55rem 0.7rem;
          font-size: 0.95rem;
          color: #09090b;
          outline: none;
        }
        .brutalist-input:focus { border-color: #09090b; background-color: #ffffff; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}

function UserList({ title, icon, users, meId, onRemove, onChangePassword, onRenameUser, canRemove, testIdPrefix }) {
  return (
    <div className="bg-white border-2 border-zinc-200 brutal-shadow">
      <div className="px-4 py-3 border-b-2 border-zinc-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-black tracking-tight text-zinc-950">{title}</h3>
        </div>
        <span className="font-mono text-xs text-zinc-500" data-testid={`${testIdPrefix}-count`}>{users.length}</span>
      </div>
      <ul>
        {users.length === 0 ? (
          <li className="px-4 py-6 text-center font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">
            Boş
          </li>
        ) : (
          users.map((u) => (
            <li
              key={u.id}
              data-testid={`user-${u.id}`}
              className="px-4 py-3 border-b border-zinc-100 last:border-b-0 flex items-center justify-between gap-2"
            >
              <div className="min-w-0 flex-1">
                <div className="font-bold text-zinc-950 break-words">
                  {u.display_name}
                  {u.id === meId && <span className="ml-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500">(Sen)</span>}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                  @{u.username} · {ROLE_LABEL[u.role]}
                </div>
              </div>
              {onRenameUser && (
                <button
                  data-testid={`rename-user-${u.username}`}
                  onClick={() => onRenameUser(u)}
                  className="p-2 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                  title="Görünen adı değiştir"
                >
                  <Pencil size={13} strokeWidth={2.5} />
                </button>
              )}
              {onChangePassword && (
                <button
                  data-testid={`change-user-password-${u.username}`}
                  onClick={() => onChangePassword(u)}
                  className="p-2 text-zinc-400 hover:text-violet-700 hover:bg-violet-50 transition-colors"
                  title="Şifreyi değiştir"
                >
                  <KeyRound size={13} strokeWidth={2.5} />
                </button>
              )}
              {canRemove && u.id !== meId && (
                <button
                  data-testid={`delete-user-${u.username}`}
                  onClick={() => onRemove(u)}
                  className="p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  title="Sil"
                >
                  <Trash2 size={14} strokeWidth={2.5} />
                </button>
              )}
            </li>
          ))
        )}
      </ul>
    </div>
  );
}
